import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Rating from "./components/Rating";
import Modal from "./components/RedirectionModal";
import { FaGoogle, FaYelp, FaFacebook, FaArrowLeft } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";

import "./Landing.css";

function redirect(url) {
  if (!/^(http|https):\/\//i.test(url)) {
    url = `https://${url}`;
  }
  window.open(url, "_blank");
}

const Landing = () => {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [redirectLink, setRedirectLink] = useState("");
  const [currentService, setCurrentService] = useState("");
  const [isRatingProvided, setIsRatingProvided] = useState(false);
  const [rating, setRating] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [animateQuestion, setAnimateQuestion] = useState(false);

  const navigate = useNavigate();
  const { client_key: clientKey } = useParams();
  const [loading, setLoading] = useState(false);
  const [clientName, setClientName] = useState(null);
  const [businessName, setBusinessName] = useState(null);
  const [links, setLinks] = useState(null);
  const [optimizationScale, setOptimizationScale] = useState(50);
  const [noLabel, setNoLabel] = useState("No");
  const [visitDetails, setVisitDetails] = useState(null);
  const [visitDetailsLoading, setVisitDetailsLoading] = useState(false);

  const onContinue = async () => {
    setNoLabel("Next");

    redirect(redirectLink ?? "yelp.com");
    setIsModalOpen(false);
  };

  const onSubmitRating = async (recentCurrentService) => {
    console.log(
      "Logging Rating after Tap..",
      rating,
      recentCurrentService,
      currentService,
    );
    try {
      const result = await fetch(`/clients/${clientKey}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          feedback: "",
          rating: rating,
          platform: recentCurrentService || currentService || "uzeli",
        }),
      });
      const data = await result.json();
      console.log(data);
    } catch (e) {
      console.log("Error while saving platform: ", e?.message || e);
    }
  };

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response = await fetch(`/clients/${clientKey}`);
        const responseData = await response.json();
        const data = responseData?.data;
        if (data) {
          setClientName(data.client_name);
          setBusinessName(data.business_name);
          setLinks(JSON.parse(data.links));
          setOptimizationScale(data.optimization);
        }
      } catch (error) {
        console.error("Error while fetching data:", error?.message || error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientKey]);

  const allQuestions = useMemo(
    () => [
      {
        key: "yelp",
        text: "Do you use Yelp?",
        subtext: "Have you left reviews on Yelp before?",
        link: links?.yelp,
        enabled: links?.yelpEnabled && links?.yelp,
        service: "Yelp",
        icon: FaYelp,
        color: "#D32323",
        bgColor: "bg-red-50",
      },
      {
        key: "facebook",
        text: "Do you use Facebook?",
        subtext: "Leave a facebook review!",
        link: links?.facebook,
        enabled: links?.facebookEnabled && links?.facebook,
        service: "Facebook",
        icon: FaFacebook,
        color: "#1877F2",
        bgColor: "bg-blue-50",
      },
      {
        key: "google",
        text: "Do you use Google?",
        subtext: "Rate us on google and help others discover us",
        link: links?.google,
        enabled: links?.googleEnabled && links?.google,
        service: "Google",
        icon: FaGoogle,
        color: "#4285F4",
        bgColor: "bg-blue-50",
      },
    ],
    [links],
  );

  const questions = useMemo(
    () => allQuestions.filter((q) => q.enabled),
    [allQuestions],
  );

  const handleYes = async () => {
    const currentQuestion = questions[questionIndex];
    setRedirectLink(currentQuestion.link);
    console.log(
      "Selected platform:",
      currentQuestion.service,
      currentQuestion.link,
    );
    setCurrentService(currentQuestion.service);
    setIsModalOpen(true);
    console.log(
      "Continue to platform:",
      redirectLink,
      "update the db platform to: ",
      currentService,
    );
    setVisitDetailsLoading(true);
    await onSubmitRating(currentQuestion.service);
    try {
      const res = await fetch(`/clients/${clientKey}/visitdetails`);
      const json = await res.json();
      setVisitDetails(json?.data || null);
    } catch (e) {
      console.log("Error fetching service details:", e);
    } finally {
      setVisitDetailsLoading(false);
    }
  };

  const handleNo = () => {
    const nextIndex = questionIndex + 1;

    if (nextIndex < questions.length) {
      setQuestionHistory((prev) => [...prev, questionIndex]);
      setQuestionIndex(nextIndex);
      setAnimateQuestion(true);
      setTimeout(() => {
        setAnimateQuestion(false);
      }, 300);
    } else {
      navigate("/thankyou");
    }
  };

  const handleBack = () => {
    if (questionHistory.length > 0) {
      const previousIndex = questionHistory[questionHistory.length - 1];
      setQuestionHistory((prev) => prev.slice(0, -1));
      setQuestionIndex(previousIndex);
      setAnimateQuestion(true);
      setTimeout(() => {
        setAnimateQuestion(false);
      }, 300);
    } else {
      setIsRatingProvided(false);
      setRating(0);
    }
  };

  const handleRatingProvided = (providedRating) => {
    setRating(providedRating);
    setIsRatingProvided(true);

    if (
      providedRating <= 3 ||
      (providedRating === 4 && optimizationScale > 50)
    ) {
      navigate(
        `/${clientKey}/feedback?rating=${providedRating}&platform=uzeli`,
      );
    } else if (questions.length === 0) {
      navigate("/thankyou");
    } else {
      setAnimateQuestion(true);
      setTimeout(() => {
        setAnimateQuestion(false);
        setQuestionIndex(0);
        setQuestionHistory([]);
      }, 300);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-14 h-14 border-4 border-blue-100 border-t-blue-500 rounded-full animate-spin" />
            <HiSparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-500 text-lg" />
          </div>
          <p className="mt-3 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[questionIndex];
  const CurrentIcon = currentQuestion?.icon;
  const currentColor = currentQuestion?.color;
  const currentBgColor = currentQuestion?.bgColor;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="flex flex-col justify-center items-center min-h-screen py-6 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-gray-200/50 overflow-hidden">
          <div className="relative bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-5 text-white">
            {isRatingProvided && (
              <button
                onClick={handleBack}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-white/20 transition-colors"
              >
                <FaArrowLeft size={16} />
              </button>
            )}
            <h1 className="text-xl font-semibold text-center">
              Hello, {clientName?.split(" ")[0] ?? "there"}!
            </h1>
          </div>

          <div className="p-6">
            {!isRatingProvided ? (
              <>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-3xl">⭐</span>
                  </div>
                </div>

                <div className="text-center mb-5">
                  <p className="text-gray-500 text-sm">
                    Thank you for visiting
                  </p>
                  <p className="text-lg font-semibold text-gray-800 mt-1">
                    {businessName ?? "our business"}
                  </p>
                  <p className="text-gray-600 mt-3">
                    How would you rate your experience?
                  </p>
                </div>

                <div className="flex justify-center mb-5">
                  <Rating rating={rating} setRating={setRating} />
                </div>

                <button
                  onClick={async () => {
                    await onSubmitRating();
                    handleRatingProvided(rating);
                  }}
                  disabled={rating === 0}
                  className={`
                    w-full py-3.5 rounded-xl text-lg font-semibold transition-all duration-300
                    ${
                      rating > 0
                        ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200 hover:shadow-xl active:scale-[0.98]"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }
                  `}
                >
                  {rating > 0 ? "Submit Rating" : "Tap a star to rate"}
                </button>

                <p className="text-center text-sm text-gray-400 mt-4">
                  Your feedback helps us improve
                </p>
              </>
            ) : (
              <>
                <div className="flex justify-center mb-4">
                  <div
                    className={`w-16 h-16 ${currentBgColor} rounded-full flex items-center justify-center transition-all duration-300`}
                  >
                    {CurrentIcon && (
                      <CurrentIcon size={32} style={{ color: currentColor }} />
                    )}
                  </div>
                </div>

                <div
                  className={`text-center mb-6 min-h-[80px] transition-all duration-300 ${animateQuestion ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}
                >
                  <p className="text-xl font-semibold text-gray-800 mb-1">
                    {currentQuestion?.text}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {currentQuestion?.subtext}
                  </p>
                </div>

                <div className="flex justify-center gap-2 mb-5">
                  {questions.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        idx === questionIndex
                          ? "w-6 bg-blue-500"
                          : idx < questionIndex
                            ? "w-1.5 bg-blue-300"
                            : "w-1.5 bg-gray-200"
                      }`}
                    />
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleNo}
                    className="flex-1 py-3.5 rounded-xl text-base font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-200 active:scale-[0.98]"
                  >
                    {noLabel}
                  </button>
                  <button
                    onClick={handleYes}
                    className="flex-1 py-3.5 rounded-xl text-base font-semibold text-white transition-all duration-200 active:scale-[0.98] shadow-lg hover:shadow-xl"
                    style={{
                      background: `linear-gradient(135deg, ${currentColor}, ${currentColor}dd)`,
                      boxShadow: `0 8px 20px -5px ${currentColor}40`,
                    }}
                  >
                    Yes
                  </button>
                </div>

                <button
                  onClick={() => navigate("/thankyou")}
                  className="w-full mt-4 py-2 text-gray-400 hover:text-gray-600 text-sm transition-colors"
                >
                  Skip
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onContinue={onContinue}
        service={currentService}
        businessName={businessName}
        visitDetails={visitDetails}
        visitDetailsLoading={visitDetailsLoading}
      />
    </div>
  );
};

export default Landing;
